import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  KnowledgebaseArticle,
  KnowledgebaseCategory,
} from '../models/knowledgebase-article.model';
import { Firestore, collection, deleteDoc, doc, orderBy, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { FIREBASE_FIRESTORE, collectionData, docData } from '@awo-care/shared-firebase';

@Injectable({ providedIn: 'root' })
export class KnowledgebaseService {
  private readonly firestore = inject(FIREBASE_FIRESTORE);
  private readonly collectionName = 'knowledgebase';

  /**
   * Lädt KB-Artikel. Die 'admin'-Kategorie ist nur für Admins zugänglich
   * (Firestore-Rules) — Nicht-Admins müssen sie aus der List-Query ausschließen,
   * sonst verstößt die Query gegen die Read-Rule und schlägt komplett fehl.
   *
   * @param category Optional auf eine Kategorie einschränken.
   * @param includeAdmin Nur für Admins true setzen, um admin-Artikel mitzuladen.
   */
  getArticles(
    category?: KnowledgebaseCategory,
    includeAdmin = false
  ): Observable<KnowledgebaseArticle[]> {
    const ref = collection(this.firestore, this.collectionName);
    let q;
    if (category) {
      q = query(
        ref,
        where('category', '==', category),
        orderBy('sortOrder', 'asc')
      );
    } else if (includeAdmin) {
      q = query(ref, orderBy('category'), orderBy('sortOrder', 'asc'));
    } else {
      q = query(
        ref,
        where('category', '!=', 'admin'),
        orderBy('category'),
        orderBy('sortOrder', 'asc')
      );
    }
    return collectionData(q, { idField: 'id' }) as Observable<
      KnowledgebaseArticle[]
    >;
  }

  getArticle(docId: string): Observable<KnowledgebaseArticle | undefined> {
    const ref = doc(this.firestore, this.collectionName, docId);
    return docData(ref, { idField: 'id' }) as Observable<
      KnowledgebaseArticle | undefined
    >;
  }

  async saveArticle(
    article: Partial<KnowledgebaseArticle> & {
      slug: string;
      category: KnowledgebaseCategory;
    },
    userId: string,
    userEmail: string
  ): Promise<string> {
    const docId = article.id || `${article.category}-${article.slug}`;
    const ref = doc(this.firestore, this.collectionName, docId);

    const data: Record<string, unknown> = {
      ...article,
      lastModifiedOn: serverTimestamp(),
      lastModifiedBy: userId,
      lastModifiedByEmail: userEmail,
    };

    if (!article.id) {
      data['createdOn'] = serverTimestamp();
      data['createdBy'] = userId;
      data['createdByEmail'] = userEmail;
    }

    delete data['id'];

    await setDoc(ref, data, { merge: true });
    return docId;
  }

  async deleteArticle(docId: string): Promise<void> {
    const ref = doc(this.firestore, this.collectionName, docId);
    await deleteDoc(ref);
  }

  sourceToDocId(sourceTitle: string): string | null {
    const match = sourceTitle.match(
      /^(customer|employee|admin)\/(.+?)(?:\.md)?$/
    );
    if (!match) return null;
    return `${match[1]}-${match[2]}`;
  }
}
